import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import {
  getAgencyClinics,
  getAgencyEnrollmentCount,
  type AgencyClinicResponse,
} from '../services/clinicsApi';
import PhysiciansSection from './Admin/PhysiciansSection';
import InternalReferralSourcesSection from './Admin/InternalReferralSourcesSection';
import SchedulingPointsSection from './Admin/SchedulingPointsSection';
import HhrgCostsSection from './Admin/HhrgCostsSection';
import TaskTypesSection from './Admin/TaskTypesSection';
import UserLicenseTypesSection from './Admin/UserLicenseTypesSection';
import FormTemplatesSection from './Admin/FormTemplatesSection';
import ClinicInfoSection from './Admin/ClinicInfoSection';
import InsuranceSection from './Admin/InsuranceSection';
import {
  ChartWorkflowSection,
  PatientCreationSection,
} from '../components/admin';
import { PermissionSlug } from '../constants/featureFlags';
import { visibleAdminSections } from '../constants/adminSections';
import { useAdminSectionAccess } from '../hooks/useAdminSectionAccess';

import { EnrollmentsTab } from '../components/admin/EnrollmentsTab';

const Admin: React.FC = () => {
  const {
    user,
    showError,
    hasAnyPermission,
    hasPermission,
    hasFeatureFlag,
    fetchAgencyClinics,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      showError: state.showError,
      hasAnyPermission: state.hasAnyPermission,
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      fetchAgencyClinics: state.fetchAgencyClinics,
    }))
  );

  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  const [physiciansRefreshToken, setPhysiciansRefreshToken] = useState(0);
  const [
    internalReferralSourcesRefreshToken,
    setInternalReferralSourcesRefreshToken,
  ] = useState(0);
  const [insurancesRefreshToken, setInsurancesRefreshToken] = useState(0);

  // Clinics list — shared across Admin sections (physicians, insurance, etc.).
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const isKinnserEnabled = user?.agency?.is_kinnser_enabled ?? false;
  const isRogerEmrEnabled = user?.agency?.is_rogeremr_enabled ?? false;

  const [searchParams, setSearchParams] = useSearchParams();

  // A section shows when the user can see it at all. `editSlug` counts on its
  // own: an edit-only grant still has to render the thing it edits.
  //
  // Not memoized: `hasAnyPermission` is a stable store function that reads the
  // permission list at call time, so a memo keyed on it would never recompute
  // and the tab strip would keep the old answer after a mid-session grant or
  // revoke, while the section-level useAdminSectionAccess calls already show
  // the new one. It filters eight entries.
  const visibleSections = visibleAdminSections(
    hasAnyPermission,
    isRogerEmrEnabled,
    hasFeatureFlag
  );

  // Through the hook like every extracted section, so the section table stays
  // the one place a slug pair is named.
  const { canEdit: canEditInsurance } = useAdminSectionAccess('insurance');

  const requestedSection = searchParams.get('section');
  const activeSection =
    visibleSections.find(section => section.id === requestedSection)?.id ??
    visibleSections[0]?.id ??
    '';

  // Fetched here rather than inside the tab: the tab only mounts when it is
  // selected, and the whole point of the count is to be readable without
  // opening it. Its own endpoint answers with a number instead of every row.
  const showsEnrollments = visibleSections.some(
    section => section.id === 'enrollments'
  );
  const [enrollmentCount, setEnrollmentCount] = useState<number | null>(null);
  // Read, not depended on: the effect asks once, and switching tabs is not a
  // reason to ask again. With `activeSection` in the dependencies every hop
  // around Admin re-ran a query that loads every clinic, record and payer to
  // return one integer.
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;
  useEffect(() => {
    if (!showsEnrollments) return;
    // The mounted tab reports its own count, and both answers write the same
    // state — whichever landed second would win. So when the tab is already
    // open, let it be the one source. Checked again when the answer lands: the
    // tab may have been opened while this was in flight.
    if (activeSectionRef.current === 'enrollments') return;
    let cancelled = false;
    void getAgencyEnrollmentCount()
      .then(count => {
        if (!cancelled && activeSectionRef.current !== 'enrollments') {
          setEnrollmentCount(count);
        }
      })
      // Swallowed on purpose: a count that will not load is a missing badge,
      // not something to interrupt the Admin page for. The tab itself reports
      // its own failures.
      .catch(error => console.error('Failed to load enrollment count', error));
    return () => {
      cancelled = true;
    };
  }, [showsEnrollments]);

  const handleSectionChange = (sectionId: string) => {
    // `replace` so tabbing around does not fill the back button with each stop.
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set('section', sectionId);
        return next;
      },
      { replace: true }
    );
  };

  const loadClinics = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingClinics(true);
    try {
      const data = await getAgencyClinics(user.agency.id);
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
      showError('Failed to load clinic data');
    } finally {
      setIsLoadingClinics(false);
    }
  };

  /** Refresh Admin list and global store so intake / other routes see clinic edits (store caches ~5m). */
  const loadClinicsAndSyncStore = async () => {
    await loadClinics();
    if (user?.agency?.id) {
      await fetchAgencyClinics(user.agency.id, { force: true });
    }
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadClinics();
    }
  }, [user?.agency?.id]);

  // Rules of Hooks: permission check must come after all hook calls
  // The same list the tab strip renders, not the raw slug array: holding an
  // EMR-only section's slug at an agency without the EMR product is not access
  // to anything, and letting it through here opened a page with no tabs.
  if (visibleSections.length === 0) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don't have permission to access Admin features. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold'>Admin Panel</h1>
        <p className='text-muted-foreground'>
          Administrative functions and system configuration.
        </p>
      </div>

      <Tabs
        value={activeSection}
        onValueChange={handleSectionChange}
        className='w-full'
      >
        {/* Underline tabs, matching the Episode Manager's Tasks/Documents bar
            rather than the pill TabsList: this page sits at the same level in
            the app and two different tab treatments read as two conventions. */}
        <TabsList className='h-auto w-full justify-start gap-1.5 overflow-x-auto rounded-none border-b border-border/60 bg-transparent p-0'>
          {visibleSections.map(section => (
            <TabsTrigger
              key={section.id}
              value={section.id}
              className='shrink-0 whitespace-nowrap rounded-none border-b-2 border-transparent bg-transparent px-3 py-2 text-sm font-medium text-muted-foreground shadow-none transition-all hover:border-border hover:bg-muted/30 hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
            >
              {section.label}
              {section.id === 'enrollments' &&
                enrollmentCount != null &&
                enrollmentCount > 0 && (
                  <Badge
                    variant='secondary'
                    className='ml-1.5 inline-flex h-4 min-w-4 items-center justify-center px-1.5 py-0 text-[10px]'
                  >
                    {enrollmentCount}
                    <span className='sr-only'> needing action</span>
                  </Badge>
                )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='clinic-info' className='mt-4 space-y-6'>
          <ClinicInfoSection
            clinics={clinics}
            isLoadingClinics={isLoadingClinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            isKinnserEnabled={isKinnserEnabled}
            onReloadClinics={loadClinicsAndSyncStore}
            onSyncComplete={() => {
              setPhysiciansRefreshToken(token => token + 1);
              setInternalReferralSourcesRefreshToken(token => token + 1);
              setInsurancesRefreshToken(token => token + 1);
            }}
          />
        </TabsContent>

        <TabsContent value='insurance' className='mt-4 space-y-6'>
          <InsuranceSection
            clinics={clinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            refreshToken={insurancesRefreshToken}
          />
        </TabsContent>

        <TabsContent value='enrollments' className='mt-4 space-y-6'>
          <EnrollmentsTab
            canRefresh={canEditInsurance}
            onCountChange={setEnrollmentCount}
          />
        </TabsContent>

        <TabsContent value='physicians' className='mt-4 space-y-6'>
          <PhysiciansSection
            clinics={clinics}
            isSuperUser={isSuperUser}
            isRogerEmrEnabled={isRogerEmrEnabled}
            refreshToken={physiciansRefreshToken}
          />
        </TabsContent>

        <TabsContent value='referral-sources' className='mt-4 space-y-6'>
          <InternalReferralSourcesSection
            clinics={clinics}
            refreshToken={internalReferralSourcesRefreshToken}
          />
        </TabsContent>

        <TabsContent value='chart-workflow' className='mt-4 space-y-6'>
          <ChartWorkflowSection />
        </TabsContent>

        <TabsContent value='patient-creation' className='mt-4 space-y-6'>
          <PatientCreationSection />
        </TabsContent>

        <TabsContent value='hhrg-costs' className='mt-4 space-y-6'>
          <HhrgCostsSection clinics={clinics} />
        </TabsContent>

        <TabsContent value='scheduling-points' className='mt-4 space-y-6'>
          <SchedulingPointsSection
            clinics={clinics}
            isLoadingClinics={isLoadingClinics}
          />
        </TabsContent>

        {/* Staff license types. Not gated on isRogerEmrEnabled: staff
            credential compliance is not an EMR-only concern, and User Manager
            is shared with intake-only agencies. */}
        <TabsContent value='user-license-types' className='mt-4 space-y-6'>
          <UserLicenseTypesSection />
        </TabsContent>

        <TabsContent value='task-types' className='mt-4 space-y-6'>
          <TaskTypesSection />
        </TabsContent>

        <TabsContent value='form-templates' className='mt-4 space-y-6'>
          <FormTemplatesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
